import { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const MAX_DISPLAY_LENGTH = 300;

/** Compact markdown components sized for table cells */
const cellComponents: Components = {
  p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  a: ({ href, children }) => (
    <a href={href} className="text-primary underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc list-inside mb-1 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-1 last:mb-0">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  code: ({ children }) => (
    <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{children}</code>
  ),
  h1: ({ children }) => <p className="font-semibold mb-1 last:mb-0">{children}</p>,
  h2: ({ children }) => <p className="font-semibold mb-1 last:mb-0">{children}</p>,
  h3: ({ children }) => <p className="font-semibold mb-1 last:mb-0">{children}</p>,
  pre: ({ children }) => <pre className="font-mono text-xs mb-1 last:mb-0">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-border pl-2 italic mb-1 last:mb-0">{children}</blockquote>,
};

interface MarkdownCellProps {
  value: unknown;
}

export const MarkdownCell = memo(function MarkdownCell({ value }: MarkdownCellProps) {
  if (value == null || value === '') return null;
  const text = String(value);
  const truncated = text.length > MAX_DISPLAY_LENGTH ? text.slice(0, MAX_DISPLAY_LENGTH) + '…' : text;
  return (
    <div className="text-muted-foreground leading-tight whitespace-normal break-words">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={cellComponents}>
        {truncated}
      </ReactMarkdown>
    </div>
  );
});
